import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  isLoading: false,
  list: [],
  total: 0,
  totalPages: 0,
  currentPage: 1,
  detail: {},
  filters: {
    page: 1,
    limit: 10,
    search: "",
    healthType: "",
    minRating: 0,
    latitude: null,
    longitude: null,
    radius: null,
    emergencyService: null,
    prefecture: "",
    district: "",
  },
  selectedRowKeys: [],
  selectedRows: [],
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
};

const healthPlaceSlice = createSlice({
  name: "healthPlace",
  initialState,
  reducers: {
    // GET LIST
    getHealthPlaceListRequest: {
      reducer: (state) => ({
        ...state,
        isLoading: true,
        error: null,
      }),
      prepare: (payload) => ({ payload, meta: { thunk: true } }),
    },
    getHealthPlaceListSuccess: {
      reducer: (state, action) => {
        const { docs, totalDocs, totalPages, page } = action.payload;
        return {
          ...state,
          isLoading: false,
          list: docs || [],
          total: totalDocs || 0,
          totalPages: totalPages || 0,
          currentPage: page || 1,
          error: null,
        };
      },
      prepare: (payload, meta) => ({ payload, meta }),
    },
    getHealthPlaceListFailure: {
      reducer: (state, action) => ({
        ...state,
        isLoading: false,
        error: action.payload,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },

    // GET DETAIL
    getHealthPlaceDetailRequest: {
      reducer: (state) => ({
        ...state,
        isLoading: true,
        error: null,
      }),
      prepare: (payload) => ({ payload, meta: { thunk: true } }),
    },
    getHealthPlaceDetailSuccess: {
      reducer: (state, action) => ({
        ...state,
        isLoading: false,
        detail: action.payload,
        error: null,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },
    getHealthPlaceDetailFailure: {
      reducer: (state, action) => ({
        ...state,
        isLoading: false,
        error: action.payload,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },

    // CREATE
    createHealthPlaceRequest: {
      reducer: (state) => ({
        ...state,
        isCreating: true,
        error: null,
      }),
      prepare: (payload) => ({ payload, meta: { thunk: true } }),
    },
    createHealthPlaceSuccess: {
      reducer: (state) => ({
        ...state,
        isCreating: false,
        error: null,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },
    createHealthPlaceFailure: {
      reducer: (state, action) => ({
        ...state,
        isCreating: false,
        error: action.payload,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },

    // UPDATE
    updateHealthPlaceRequest: {
      reducer: (state) => ({
        ...state,
        isUpdating: true,
        error: null,
      }),
      prepare: (payload) => ({ payload, meta: { thunk: true } }),
    },
    updateHealthPlaceSuccess: {
      reducer: (state) => ({
        ...state,
        isUpdating: false,
        error: null,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },
    updateHealthPlaceFailure: {
      reducer: (state, action) => ({
        ...state,
        isUpdating: false,
        error: action.payload,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },

    // DELETE (SOFT)
    deleteHealthPlaceRequest: {
      reducer: (state) => ({
        ...state,
        isDeleting: true,
        error: null,
      }),
      prepare: (payload) => ({ payload, meta: { thunk: true } }),
    },
    deleteHealthPlaceSuccess: {
      reducer: (state) => ({
        ...state,
        isDeleting: false,
        error: null,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },
    deleteHealthPlaceFailure: {
      reducer: (state, action) => ({
        ...state,
        isDeleting: false,
        error: action.payload,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },

    // RESTORE
    restoreHealthPlaceRequest: {
      reducer: (state) => ({
        ...state,
        isUpdating: true,
        error: null,
      }),
      prepare: (payload) => ({ payload, meta: { thunk: true } }),
    },
    restoreHealthPlaceSuccess: {
      reducer: (state) => ({
        ...state,
        isUpdating: false,
        error: null,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },
    restoreHealthPlaceFailure: {
      reducer: (state, action) => ({
        ...state,
        isUpdating: false,
        error: action.payload,
      }),
      prepare: (payload, meta) => ({ payload, meta }),
    },

    // SET FILTERS
    setHealthPlaceFilters: (state, action) => ({
      ...state,
      filters: {
        ...state.filters,
        ...action.payload,
      },
    }),

    // SET SELECTED
    setSelectedHealthPlaces: (state, action) => {
      const { selectedRowKeys, selectedRows } = action.payload;
      return {
        ...state,
        selectedRowKeys,
        selectedRows,
      };
    },

    // CLEAR FILTERS
    clearHealthPlaceFilters: (state) => ({
      ...state,
      filters: initialState.filters,
    }),

    // CLEAR ERROR
    clearHealthPlaceError: (state) => ({
      ...state,
      error: null,
    }),
  },
});

export const {
  getHealthPlaceListRequest,
  getHealthPlaceListSuccess,
  getHealthPlaceListFailure,
  getHealthPlaceDetailRequest,
  getHealthPlaceDetailSuccess,
  getHealthPlaceDetailFailure,
  createHealthPlaceRequest,
  createHealthPlaceSuccess,
  createHealthPlaceFailure,
  updateHealthPlaceRequest,
  updateHealthPlaceSuccess,
  updateHealthPlaceFailure,
  deleteHealthPlaceRequest,
  deleteHealthPlaceSuccess,
  deleteHealthPlaceFailure,
  restoreHealthPlaceRequest,
  restoreHealthPlaceSuccess,
  restoreHealthPlaceFailure,
  setHealthPlaceFilters,
  setSelectedHealthPlaces,
  clearHealthPlaceFilters,
  clearHealthPlaceError,
} = healthPlaceSlice.actions;

export default healthPlaceSlice.reducer;
